import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API, formatINR, formatCompact } from "@/lib/api";
import AIDrawer from "@/components/AIDrawer";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { ChevronRight, ChevronDown, Download, FileSpreadsheet, Loader2, Search } from "lucide-react";

const BRACKETS = [
  { key: "b0_30", label: "0-30", color: "#0E7490" },
  { key: "b31_60", label: "31-60", color: "#3B82F6" },
  { key: "b61_90", label: "61-90", color: "#F59E0B" },
  { key: "b91_180", label: "91-180", color: "#F97316" },
  { key: "b181_365", label: "181-365", color: "#EF4444" },
  { key: "b365_plus", label: ">365", color: "#7F1D1D" },
  { key: "non_ppi", label: "Non-PPI", color: "#94A3B8" },
];
const fmtD = (v) => (v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function StockAgeingPage() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [plant, setPlant] = useState(searchParams.get("plant") || "");
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [asOf, setAsOf] = useState(searchParams.get("as_of") || "");
  const [bracketSel, setBracketSel] = useState("");
  const [expanded, setExpanded] = useState({});
  const [exporting, setExporting] = useState(false);
  const todayStr = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    setData(null);
    setError("");
    const q = asOf && asOf !== todayStr ? `?as_of=${asOf}` : "";
    api.get(`/reports/stock-ageing${q}`).then(({ data }) => setData(data)).catch((e) => setError(e.response?.data?.detail || e.message));
  }, [asOf, todayStr]);

  const rows = useMemo(() => {
    if (!data) return [];
    return data.rows.filter((r) => {
      if (plant && r.plant !== plant) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!r.material.toLowerCase().includes(q) && !r.description.toLowerCase().includes(q)) return false;
      }
      if (bracketSel && !(r[bracketSel] > 0)) return false;
      return true;
    });
  }, [data, plant, search, bracketSel]);

  const unfiltered = !plant && !search && !bracketSel;
  const totals = useMemo(() => {
    if (unfiltered && data?.totals) return data.totals; // server totals cover the full population beyond the 800 displayed rows
    const t = Object.fromEntries(BRACKETS.map((b) => [b.key, 0]));
    t.value = 0;
    rows.forEach((r) => { BRACKETS.forEach((b) => { t[b.key] += r[b.key] || 0; }); t.value += r.value; });
    return t;
  }, [rows, unfiltered, data]);

  const chartData = BRACKETS.map((b) => ({ ...b, value: totals[b.key] }));

  const exportCsv = () => {
    const head = ["Material", "Description", "Plant", "UoM", "Qty", ...BRACKETS.map((b) => b.label), "Total Value", "Last Receipt"].join(",");
    const body = rows.map((r) => [r.material, `"${r.description}"`, `"${r.plant}"`, r.uom, r.qty,
      ...BRACKETS.map((b) => r[b.key] || 0), r.value, r.last_receipt || ""].join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([head + "\n" + body], { type: "text/csv" }));
    a.download = "DeePiping_stock-ageing.csv"; a.click();
  };
  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/reports/stock-ageing/export`, { credentials: "include" });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = "DeePiping_stock-ageing.xlsx"; a.click();
    } finally { setExporting(false); }
  };

  if (error) return <div className="text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded-md p-4 max-w-lg">{error}</div>;
  if (!data) return <div className="text-slate-500 text-sm animate-pulse">Loading stock ageing…</div>;

  const k = (() => {
    if (unfiltered && data.kpis) return data.kpis; // full-population KPIs (incl. non-aged balances) — ties to Stock Reco
    const t = { closing_value: 0, recent_0_90: 0, mid_91_180: 0, slow_180_plus: 0, non_ppi: 0 };
    rows.forEach((r) => {
      t.closing_value += r.value;
      t.recent_0_90 += (r.b0_30 || 0) + (r.b31_60 || 0) + (r.b61_90 || 0);
      t.mid_91_180 += r.b91_180 || 0;
      t.slow_180_plus += (r.b181_365 || 0) + (r.b365_plus || 0);
      t.non_ppi += r.non_ppi || 0;
    });
    return t;
  })();
  const input = "bg-white border border-slate-300 rounded-md px-3 py-1.5 text-sm text-slate-800 focus:outline-none focus:border-[#B01E24]/50";
  const kpiCards = [
    ["Closing Stock Value", k.book_balance ?? k.closing_value,
     k.unaged_adjustment ? `incl. ${formatINR(k.unaged_adjustment)} non-aged balances` : "ties to the Stock Reco closing balance", "", "closing"],
    ["0–90 Days", k.recent_0_90, "recent inwards", "", "recent"],
    ["91–180 Days", k.mid_91_180, "", "border-amber-300 bg-amber-50/40", "mid"],
    ["Slow Moving (>180 days)", k.slow_180_plus, `${k.closing_value ? Math.round((100 * k.slow_180_plus) / k.closing_value) : 0}% of stock`, "border-red-200 bg-red-50/40", "slow"],
    ["Non Purchase/Production Inward", k.non_ppi, "no inward found in loaded history", "border-slate-300 bg-slate-50", "non-ppi"],
  ];

  return (
    <div className="space-y-5" data-testid="report-page-stock-ageing">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Inventory — Stock Ageing</h1>
          <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
            Closing stock aged by purchase / production inwards (latest receipts first), valued at the stock-book rate so totals tie to Stock Reco · As of {data.as_of}
            <span data-testid="stock-ageing-source-chip" className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${data.data_source === "SAP" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : data.data_source === "UPLOAD" ? "bg-sky-50 text-sky-700 border-sky-200" : "bg-amber-50 text-amber-700 border-amber-300"}`}>
              {data.data_source === "SAP" ? "SAP Live · Read-only" : data.data_source === "UPLOAD" ? "Uploaded Files" : "Mock Data"}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} data-testid="stock-ageing-export-csv-button" className="flex items-center gap-1.5 text-xs bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 rounded-md px-3 py-1.5 transition-colors"><Download size={12} /> CSV</button>
          <button onClick={exportExcel} disabled={exporting} data-testid="stock-ageing-export-excel-button" className="flex items-center gap-1.5 text-xs bg-emerald-700/90 hover:bg-emerald-600 disabled:opacity-60 text-white rounded-md px-3 py-1.5 transition-colors">{exporting ? <Loader2 size={12} className="animate-spin" /> : <FileSpreadsheet size={12} />} Excel</button>
          <AIDrawer reportKey="stock-ageing" reportTitle="Inventory Ageing" />
        </div>
      </div>

      {data.live_pending && (
        <div className="rounded-lg border border-sky-300 bg-sky-50 p-3 text-sm text-sky-800" data-testid="stock-ageing-live-pending">
          Live SAP extraction is running in the background — this page shows sample data meanwhile. Refresh in a minute or two.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2" data-testid="stock-ageing-filters">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">As on</span>
          <input type="date" value={asOf || todayStr} max={todayStr}
            onChange={(e) => setAsOf(e.target.value === todayStr ? "" : e.target.value)}
            data-testid="stock-ageing-asof-date" className={input} />
        </div>
        <select value={plant} onChange={(e) => setPlant(e.target.value)} data-testid="stock-ageing-plant-select" className={input}>
          <option value="">All Plants</option>
          {data.plants.map((p) => <option key={p}>{p}</option>)}
        </select>
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="stock-ageing-search" placeholder="Item code / description" className={`${input} pl-8 w-60`} />
        </div>
        {bracketSel && (
          <button onClick={() => setBracketSel("")} data-testid="stock-ageing-clear-bracket" className="inline-flex items-center gap-1.5 text-xs bg-[#B01E24]/10 text-[#B01E24] border border-[#B01E24]/20 rounded-full px-3 py-1">
            Bracket: {BRACKETS.find((b) => b.key === bracketSel)?.label} ✕
          </button>
        )}
        <div className="ml-auto text-xs text-slate-500">{rows.length} materials</div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="stock-ageing-kpis">
        {kpiCards.map(([label, val, sub, extra, tid]) => (
          <div key={label} className={`rounded-lg border border-slate-200 bg-white shadow-sm p-4 ${extra}`} data-testid={`sa-kpi-${tid}`}>
            <div className="text-[9.5px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
            <div className="mt-1 text-xl font-bold font-mono text-slate-900">{formatCompact(val)}</div>
            {sub && <div className="text-[10.5px] text-slate-400 mt-0.5">{sub}</div>}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-lg border border-slate-200 bg-white shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Value by age bracket</h3>
            <span className="text-[11px] text-slate-400">Click a bar to drill into that bracket</span>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartData}>
              <XAxis dataKey="label" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#94a3b8" fontSize={10} tickLine={false} axisLine={false} tickFormatter={(v) => formatCompact(v)} width={72} />
              <Tooltip contentStyle={{ backgroundColor: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 }} formatter={(v) => formatCompact(v)} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
              <Bar dataKey="value" radius={[3, 3, 0, 0]} onClick={(d) => setBracketSel(bracketSel === d.key ? "" : d.key)} className="cursor-pointer">
                {chartData.map((b) => <Cell key={b.key} fill={b.color} opacity={bracketSel && bracketSel !== b.key ? 0.3 : 1} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5" data-testid="stock-ageing-brackets-panel">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Brackets &amp; categories</h3>
          <div className="space-y-2">
            {chartData.map((b) => (
              <div key={b.key} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2 text-slate-600"><span className="w-2.5 h-2.5 rounded-full" style={{ background: b.color }} />{b.label}</span>
                <span className="font-mono text-slate-700">{formatCompact(b.value)}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-slate-100 text-[11px] text-slate-400">Price basis: stock-book rate (ties to Stock Reco closing) · total value {formatCompact(totals.value)}</div>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-800">Drill-down</h3>
          <p className="text-[11px] text-slate-400">Expand a material to see the receipt documents (material doc) that make up its aged stock</p>
        </div>
        <div className="overflow-x-auto report-scroll">
          <table className="w-full text-[12.5px]" data-testid="stock-ageing-table">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="w-8" />
                <th className="text-left px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Material</th>
                {BRACKETS.map((b) => <th key={b.key} className="text-right px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">{b.label}</th>)}
                <th className="text-right px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Total</th>
                <th className="text-right px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Qty</th>
                <th className="text-right px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">Last Receipt</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 300).map((r) => {
                const id = `${r.material}|${r.plant}`;
                const main = (
                  <tr key={id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`sa-row-${r.material}`}
                    onClick={() => setExpanded((e) => ({ ...e, [id]: !e[id] }))}>
                    <td className="pl-3 text-slate-400">{expanded[id] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className="font-mono text-[11px] text-[#B01E24]">{r.material}</span>
                      <span className="text-slate-600 ml-2">{r.description}</span>
                      <span className="text-[10.5px] text-slate-400 ml-2">{r.plant}</span>
                    </td>
                    {BRACKETS.map((b) => <td key={b.key} className={`px-3 py-2 text-right font-mono text-[12px] whitespace-nowrap ${r[b.key] ? "text-slate-600" : "text-slate-300"}`}>{r[b.key] ? formatCompact(r[b.key]) : "—"}</td>)}
                    <td className="px-3 py-2 text-right font-mono text-[12px] font-semibold text-slate-900 whitespace-nowrap">{formatCompact(r.value)}</td>
                    <td className="px-3 py-2 text-right font-mono text-[12px] text-slate-500">{r.qty.toLocaleString("en-IN")} {r.uom}</td>
                    <td className="px-3 py-2 text-right text-[11.5px] text-slate-500 whitespace-nowrap">{fmtD(r.last_receipt)}</td>
                  </tr>
                );
                if (!expanded[id]) return main;
                return [main, (
                  <tr key={`${id}-rc`} className="border-b border-slate-100 bg-slate-50/70">
                    <td />
                    <td colSpan={BRACKETS.length + 4} className="px-3 py-2">
                      {r.receipts.length === 0 ? (
                        <div className="text-[11.5px] text-slate-400">No inward receipts found in the loaded history — shown as Non-PPI.</div>
                      ) : (
                        <table className="w-full text-[11.5px]">
                          <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400">
                            <th className="text-left py-1">Receipt Date</th><th className="text-left py-1">Mat. Doc</th>
                            <th className="text-left py-1">Mvt</th><th className="text-right py-1">Qty Allocated</th><th className="text-right py-1">Value</th>
                          </tr></thead>
                          <tbody>
                            {r.receipts.map((rc, j) => (
                              <tr key={j} className="border-t border-slate-200/60">
                                <td className="py-1">{fmtD(rc.date)}</td>
                                <td className="py-1 font-mono text-slate-500">{rc.mat_doc}</td>
                                <td className="py-1 font-mono text-slate-500">{rc.mtype}</td>
                                <td className="py-1 text-right font-mono">{rc.qty.toLocaleString("en-IN")}</td>
                                <td className="py-1 text-right font-mono">{formatINR(rc.value)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </td>
                  </tr>
                )];
              })}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 border-t border-slate-200 font-semibold text-[12px] text-slate-900">
                <td />
                <td className="px-3 py-2.5">TOTAL</td>
                {BRACKETS.map((b) => <td key={b.key} className="px-3 py-2.5 text-right font-mono whitespace-nowrap">{formatCompact(totals[b.key])}</td>)}
                <td className="px-3 py-2.5 text-right font-mono whitespace-nowrap">{formatCompact(totals.value)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}
